import { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import { Icons } from "assets";
import { toastMessage } from "shared/components/common/toast";
import styles from "./style.module.scss";
import classNames from "classnames";
import CustomFileUpload from "shared/components/common/customFileUpload";
import {
  CreateAdvertisementService,
  UpdateAdvertisementService,
} from "shared/services/advertisement";
import CustomSelect from "shared/components/common/customSelect";
import AdvertisementListings from "shared/components/dashboard/dashboardListings/adListings";

const Images = () => {
  const [createMode, setCreateMode] = useState(false);
  const [advertisementId, setAdvertisementId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);

  const validationSchema = Yup.object({
    screen: Yup.string().required("Select a screen"),
    link: Yup.string().required("Link is required"),
    image: Yup.mixed().required("Advertisement is required"),
  });

  const formik = useFormik({
    initialValues: {
      screen: "",
      link: "",
      image: null as File | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append("link", values.link);
        formData.append("screen", values.screen);

        if (values.image) {
          formData.append("image", values.image);
        }

        if (advertisementId) {
          await UpdateAdvertisementService(advertisementId, formData);
          toastMessage("success", "Advertisement updated successfully!");
        } else {
          await CreateAdvertisementService(formData);
          toastMessage("success", "Advertisement created successfully!");
        }
        formik.resetForm();
        setCreateMode(false);
        setUploadedFile(null);
      } catch (error) {
        console.error("Error during advertisement creation:", error);
        toastMessage("error", "Failed to create advertisement.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCreateAdvertisement = () => {
    setCreateMode(true);
    formik.resetForm();
  };

  const handleCancel = () => {
    setCreateMode(false);
    setAdvertisementId(null);
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      formik.setFieldValue("image", file);
      setUploadedFile(URL.createObjectURL(file));
    }
  };

  // @ts-ignore
  const handleUpdate = async (data: {
    id: number;
    link: string;
    screen: string;
    image: { fullpath: string | null };
  }) => {
    try {
      setCreateMode(true);
      setAdvertisementId(data.id);

      const selectedScreen = screenOptions.find(
        (option) => option.value === data.screen
      );

      formik.setValues({
        screen: selectedScreen?.value || "",
        link: data.link || "",
        image: null,
      });

      setUploadedFile(data.image?.fullpath || null);
    } catch (error) {
      console.error(
        "Error while fetching advertisement details for update:",
        error
      );
      toastMessage(
        "error",
        "Failed to fetch advertisement details for update."
      );
    }
  };

  const screenOptions = [
    { id: 0, value: "homepage", title: "Home Page" },
    { id: 1, value: "search", title: "Search" },
    { id: 2, value: "productDetail", title: "Product Detail" },
  ];

  return (
    <div className={classNames(styles.sectionContainer)}>
      {createMode ? (
        <>
          <div>
            <CustomButton
              Icon={Icons.ArrowLeft}
              IconDirection="left"
              iconStyle="mr-2"
              title="Back"
              containerStyle="outlined-btn-primary mb-3 px-2"
              onClick={handleCancel}
            />
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className={classNames(styles.body, "grid grid-cols-12 gap-2")}
          >
            <div className="col-span-6">
              <CustomSelect
                options={screenOptions}
                value={formik.values.screen}
                label="Screen"
                onChange={(selectedOption) => {
                  formik.setFieldValue("screen", selectedOption.value);
                }}
                error={
                  formik.touched.screen && formik.errors.screen
                    ? formik.errors.screen
                    : ""
                }
              />
            </div>
            <div className="col-span-6">
              <CustomInput
                required
                label="Link"
                placeholder="Enter link"
                name="link"
                value={formik.values.link}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.link && formik.errors.link
                    ? formik.errors.link
                    : ""
                }
              />
            </div>

            <div className="col-span-12">
              <CustomFileUpload
                label="Upload Advertisement"
                required
                defaultImage={uploadedFile}
                // @ts-ignore
                onFileChange={handleFileChange}
              />
            </div>

            <div className="flex justify-end mt-3 w-full col-span-12">
              <CustomButton
                type="submit"
                loading={loading}
                title={
                  advertisementId ? "Update Advertisement" : "Add Advertisement"
                }
              />
            </div>
          </form>
        </>
      ) : (
        <div className={classNames(styles.header)}>
          <div className="ms-auto w-fit">
            <CustomButton
              onClick={handleCreateAdvertisement}
              title="Add Advertisement"
            />
          </div>
          <div className="mt-4">
            <AdvertisementListings
              // @ts-ignore
              handleUpdate={handleUpdate}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;
